import { useCallback, useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import { getInstallations, getId } from "@firebase/installations";
import config from "../config/config";
import {
  getPlatform,
  isInStandaloneMode,
  isIos,
} from "../services/mobileUtils";
import { useAuth } from "../features/auth/hooks/useAuth";
import { useMutation } from "@apollo/client";
import { REGISTER_TOKEN_MUTATION } from "../features/user/api/mutations";

const firebaseConfig = {
  apiKey: config.firebase.apiKey,
  authDomain: config.firebase.authDomain,
  projectId: config.firebase.projectId,
  storageBucket: config.firebase.storageBucket,
  messagingSenderId: config.firebase.messagingSenderId,
  appId: config.firebase.appId,
};

const isServiceWorkerSupported = () => {
  return "serviceWorker" in navigator;
};

let app: firebase.app.App | null = null;
let messaging: firebase.messaging.Messaging | null = null;

if (isServiceWorkerSupported()) {
  app = firebase.initializeApp(firebaseConfig);
  messaging = firebase.messaging();
}

// Get Firebase Installation ID - this is stable for the app installation
async function getFirebaseInstallationId(): Promise<string> {
  if (!app) {
    throw new Error("Firebase app not initialized");
  }

  try {
    const installations = getInstallations(app);
    const installationId = await getId(installations);
    return installationId;
  } catch (error) {
    console.error("Error getting Firebase Installation ID:", error);
    throw error;
  }
}

export interface NotificationData {
  title: string;
  body: string;
  image: string | null;
  chatId?: string;
  clickAction?: string;
  identityId?: string;
  /** Backend notification category, e.g. "gallery_photo_added". */
  type?: string;
  eventId?: string;
}

export const useFirebaseMessaging = () => {
  const { isAuthenticated } = useAuth();
  const [registerToken] = useMutation(REGISTER_TOKEN_MUTATION);
  const [notification, setNotification] = useState<NotificationData | null>(
    null
  );
  const [installationId, setInstallationId] = useState<string | null>(null);

  // Get installation ID on component mount
  useEffect(() => {
    if (!app) return;

    getFirebaseInstallationId()
      .then((id) => {
        setInstallationId(id);
      })
      .catch((err) => {
        console.error("Failed to get installation ID:", err);
      });
  }, []);

  const isNotificationSupported = () => {
    return (
      typeof window !== "undefined" &&
      "Notification" in window &&
      window.Notification !== undefined
    );
  };

  const requestPushPermission = useCallback(async () => {
    console.log("🔔 requestPushPermission called");
    
    if (!isNotificationSupported()) {
      console.error("This browser does not support notifications");
      return false;
    }

    // Check if we're in a secure context
    if (!window.isSecureContext) {
      console.error(
        "Notifications require a secure context (HTTPS or localhost)"
      );
      return false;
    }

    if (!messaging || !isServiceWorkerSupported()) {
      console.error("Push notifications are not supported in this browser", {
        messaging: !!messaging,
        serviceWorkerSupported: isServiceWorkerSupported()
      });
      return false;
    }

    if (isIos() && !isInStandaloneMode()) {
      console.log("🔔 iOS not in standalone mode - skipping");
      return false;
    }

    try {
      const currentPermission = Notification.permission;
      console.log("🔔 Current permission before request:", currentPermission);

      if (currentPermission === "denied") {
        return {
          canRequest: false,
          needsSettingsReset: true,
          browserInstructions: `
            Per attivare le notifiche:
            1. Clicca sull'icona del lucchetto nella barra degli indirizzi
            2. Clicca su "Impostazioni sito"
            3. Trova "Notifiche" e cambia da "Blocca" a "Chiedi"
            4. Ricarica la pagina
          `,
        };
      }

      console.log("🔔 Requesting notification permission...");
      const permission = await Notification.requestPermission();
      console.log("🔔 Permission result:", permission);

      if (permission === "granted") {
        console.log("🔔 Getting Firebase installation ID...");
        // Make sure we have an installation ID
        const deviceId = await getFirebaseInstallationId();
        console.log("🔔 Device ID:", deviceId);

        console.log("🔔 Getting FCM token...");
        const newToken = await messaging?.getToken({
          vapidKey: config.firebase.vapidKey,
        });
        console.log("🔔 FCM Token received:", newToken ? "✓" : "✗");

        if (!newToken) {
          console.error("🔔 Failed to get FCM token");
          return false;
        }

        const platform = getPlatform();
        console.log("🔔 Registering token with backend...", { platform });

        await registerToken({
          variables: {
            pushToken: newToken,
            deviceId, // Use Firebase Installation ID
            platform,
          },
        });

        localStorage.setItem(`gj_push_token_${deviceId}`, newToken);
        console.log("🔔 ✓ Token registered successfully!");
        return true;
      }

      console.log("🔔 Permission not granted:", permission);
      return false;
    } catch (error) {
      console.error("🔔 Error during permission request:", error);
      return false;
    }
  }, [registerToken]);

  useEffect(() => {
    if (!isAuthenticated || !messaging || !installationId) return;

    const storedTokenKey = `gj_push_token_${installationId}`;

    const checkAndUpdateToken = async () => {
      try {
        if (!isNotificationSupported()) {
          return;
        }

        if (Notification.permission !== "granted") {
          return;
        }

        const currentToken = await messaging?.getToken({
          vapidKey: config.firebase.vapidKey,
        });

        if (!currentToken) {
          return;
        }

        // Only call the backend when the token has actually changed.
        const storedToken = localStorage.getItem(storedTokenKey);
        if (currentToken === storedToken) {
          return;
        }

        const platform = getPlatform();

        await registerToken({
          variables: {
            pushToken: currentToken,
            deviceId: installationId,
            platform,
          },
        });

        localStorage.setItem(storedTokenKey, currentToken);
      } catch (error) {
        console.error("Error checking token:", error);
      }
    };

    // Foreground message handler
    const messageUnsubscribe = messaging.onMessage((payload) => {
      setNotification({
        // data map takes priority; fall back to notification object (set by firebase_provider)
        title: payload.data?.title || payload.notification?.title || "GioJoy",
        body: payload.data?.body || payload.notification?.body || "",
        image: payload.data?.image || null,
        chatId: payload.data?.chatId || undefined,
        clickAction: payload.data?.click_action || undefined,
        identityId: payload.data?.identityId || undefined,
        // Needed so the foreground toast can deep-link the same way the
        // service worker's notificationclick handler does.
        type: payload.data?.type || undefined,
        eventId: payload.data?.eventId || undefined,
      });
    });

    // Check token immediately and then set up interval
    checkAndUpdateToken();
    const tokenCheckInterval = setInterval(checkAndUpdateToken, 1000 * 60 * 60);

    // Cleanup
    return () => {
      messageUnsubscribe();
      clearInterval(tokenCheckInterval);
    };
  }, [isAuthenticated, registerToken, installationId]);

  return { notification, requestPushPermission };
};
